import { ChangeEvent, useState } from 'react';
import {
  Card,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  TextField,
  styled,
} from '@mui/material';
import {
  IPreFilter,
  StyledSelect,
  USA_STATES,
  menuItemsSortByLabel,
  CsaFilterOptions,
  DeaFilterFunction,
  Tooltip,
  TooltipTrigger,
  side,
} from '../common';
import { CsaFilterButtons } from './CsaFilterButtons';
import { flow } from 'fp-ts/function';

export const CsaFilterForm = ({
  emptyCsaSrchResult,
  searchByFilter,
  tooltipValue,
  toggleTooltip,
  offTooltip,
  onClear,
}: {
  emptyCsaSrchResult: boolean;
  searchByFilter: DeaFilterFunction;
  tooltipValue: (tooltip: string) => boolean;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
  onClear: () => void;
}) => {
  const [chainCodeFlag, setChainCodeFlag] = useState(true);
  const [deaNumber, setDeaNumber] = useState('');
  const [ssn, setSsn] = useState('');
  const [taxId, setTaxId] = useState('');
  const [npi, setNpi] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [filterOption, setFilterOption] = useState<CsaFilterOptions>(
    CsaFilterOptions.TaxId
  );

  const preparePayload = (): IPreFilter => {
    const pl: IPreFilter = {
      chainCodeFlag: chainCodeFlag,
      deaNumber: deaNumber || null,
      ssn: ssn || null,
      taxId: taxId || null,
      npi: npi || null,
      city: city || null,
      state: stateCode || null,
      postalCd: zipCode || null,
    };
    return pl;
  };

  const clearForm = () => {
    setDeaNumber('');
    setSsn('');
    setTaxId('');
    setNpi('');
    setCity('');
    setStateCode('');
    setZipCode('');
    onClear();
  };

  const clearDropDown = () => {
    setTaxId('');
    setSsn('');
    setDeaNumber('');
    setNpi('');
  };

  const handleChangeText = (e: any) => {
    const value =
      (e as React.ChangeEvent<HTMLInputElement> as any).target.value || '';
    switch (filterOption) {
      case CsaFilterOptions.TaxId:
        setTaxId(value);
        break;
      // case CsaFilterOptions.SSN:
      //   setSsn(value);
      //   break;
      case CsaFilterOptions.NPI:
        setNpi(value);
        break;
      case CsaFilterOptions.DEANum:
        setDeaNumber(value);
        break;
      default:
        setTaxId(value);
        break;
    }
  };

  const getTextValue = () => {
    switch (filterOption) {
      case CsaFilterOptions.TaxId:
        return taxId;
      // case CsaFilterOptions.SSN:
      //   return ssn;
      case CsaFilterOptions.NPI:
        return npi;
      case CsaFilterOptions.DEANum:
        return deaNumber;
      default:
        return taxId;
    }
  };

  const handleChangeChainCode = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const flag = event.target.checked;
    setChainCodeFlag(flag);
  };
  return (
    <FilterForm>
      <Wrapper>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={chainCodeFlag}
                onChange={handleChangeChainCode}
                name="chainCodeFlag"
                color="primary"
              />
            }
            label="Chaincode Business"
          />
        </FormGroup>

        <Spacer />

        <StyledSelect
          labelId="filter-code-option"
          value={filterOption}
          onChange={e => {
            e.preventDefault();
            clearDropDown();
            setFilterOption(e.target.value as CsaFilterOptions);
          }}
          variant="outlined">
          <MenuItem value={CsaFilterOptions.TaxId}>
            {CsaFilterOptions.TaxId}
          </MenuItem>
          {/* <MenuItem value={CsaFilterOptions.SSN}>
            {CsaFilterOptions.SSN}
          </MenuItem> */}
          <MenuItem value={CsaFilterOptions.NPI}>
            {CsaFilterOptions.NPI}
          </MenuItem>
          <MenuItem value={CsaFilterOptions.DEANum}>
            {CsaFilterOptions.DEANum}
          </MenuItem>
        </StyledSelect>
        <TextField
          id={filterOption}
          value={getTextValue()}
          placeholder={filterOption}
          onChange={flow(
            side((e: ChangeEvent) => e.preventDefault()),
            side(handleChangeText),
            () => setCity(''),
            () => setStateCode(''),
            () => setZipCode('')
          )}
          variant="outlined"
        />

        <Spacer>.</Spacer>

        <VerticalLine orientation="vertical" />

        <label htmlFor="city">City</label>
        <TextField
          id="city"
          value={city}
          disabled={emptyCsaSrchResult}
          onChange={e => {
            e.preventDefault();
            setCity(e.target.value);
          }}
          variant="outlined"
        />
        <label htmlFor="state-code">State</label>
        <StyledSelect
          labelId="state-code"
          value={stateCode}
          disabled={emptyCsaSrchResult}
          onChange={e => {
            e.preventDefault();
            setStateCode(e.target.value as string);
          }}
          variant="outlined">
          {menuItemsSortByLabel(USA_STATES)}
        </StyledSelect>

        <label>- OR -</label>

        <Spacer />

        <label htmlFor="zip-code">ZIP Code</label>

        <Tooltip name="requestZipcode" tooltipValue={tooltipValue}>
          <TextField
            id="zip-code"
            value={zipCode}
            disabled={emptyCsaSrchResult}
            onChange={e => {
              e.preventDefault();
              setZipCode(e.target.value);
            }}
            variant="outlined"
          />
        </Tooltip>

        <Spacer />

        <TooltipTrigger
          name="requestZipcode"
          toggleTooltip={toggleTooltip}
          offTooltip={offTooltip}
        />
      </Wrapper>
      <CsaFilterButtons
        payload={preparePayload()}
        searchHandler={searchByFilter}
        clearHandler={clearForm}
      />
    </FilterForm>
  );
};

const FilterForm = styled(Card)(({ theme }) => ({
  '&>*+*': {
    marginTop: '1rem',
  },
}));

const Wrapper = styled('article')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: `1rem`,
  alignItems: 'center',

  '@media (min-width: 40em)': {
    gridTemplateColumns: '2fr 2fr 3fr 1fr 1fr 3fr 1fr 2fr 1fr 2fr 3fr 28px',
  },

  '& > label': {
    justifySelf: 'end',
  },
  padding: '0.5rem',
}));

const Spacer = styled('span')(({ theme }) => ({
  '@media (min-width: 40em)': {
    display: 'none',
  },
}));

const VerticalLine = styled(Divider)(({ theme }) => ({
  justifySelf: 'end',
}));
